/** @format */

import React, { useState } from "react";
import { Button } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import Carousel from "react-bootstrap/Carousel";
import { useNavigate } from "react-router-dom";
import "./EditResourceModal.scss";
import ResourcesArray from "../../assets/images/editResourceModal/resources-array.svg";
import CustomiseResource from "../../assets/images/editResourceModal/customise-resource.svg";
import ShareCustomisedResource from "../../assets/images/editResourceModal/share-customised-resource.svg";
import { Resource, UserResource } from "../resources/types";
import { useCreateResourceMutation } from "../../app/services/userResource/userResourcesApi";
import { useTranslation } from "react-i18next";
import { useLocale } from "../i18n/hooks";
import BLSpinner from "../../assets/animations/BLSpinner/BLSpinner";
import HelpLink from "../helpLink/HelpLink";
import { getResourceCode } from "../../utils/GetResourceCode";
import Form from "react-bootstrap/Form";
import { Locale } from "../i18n/init";

export const CY_CHECK_TEST_ID = "cyCheck";

type Props = {
  resource: Resource | UserResource;
};

const EditResourceModal: React.FC<Props> = ({ resource }) => {
  const [show, setShow] = useState(false);
  const { t: _ } = useTranslation();
  const locale = useLocale();
  const navigate = useNavigate();

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const [createUserResource] = useCreateResourceMutation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedLocale, setSelectedLocale] = useState<Locale>(locale);

  const getUserResource = async () => {
    if (!resource.Object.qualification?.includes("mfw")) {
      return;
    }

    if (resource) {
      try {
        const code = getResourceCode(resource);
        const newUserResource = await createUserResource({
          code,
          locale: selectedLocale,
        }).unwrap();
        if (!!newUserResource) {
          handleClose();
          navigate(
            `${selectedLocale}/u/${newUserResource.User}/${code}/${newUserResource.Copy}`,
          );
          handleClose();
        } else {
          // Add error message
          handleClose();
        }
      } catch {
        // Add error message
        handleClose();
      }
    }

    setIsLoading(false);
  };

  return (
    <>
      <Button variant="btn btn-sm px-3 btn-editing" onClick={handleShow}>
        {_("Duplicate resource")}
      </Button>

      <Modal show={show} onHide={handleClose} className="edit-resource-modal">
        <Modal.Header closeButton>
          <span className="flex-grow-1">&nbsp;</span>
          <HelpLink
            locationId="begin-editing"
            className="p-0 help-link"
            position="inherit"
          />
        </Modal.Header>
        <Modal.Body>
          {!resource.Object.qualification?.includes("mfw") ? (
            <div className="p-5 text-danger">
              {_(
                "At the moment, you can only duplicate 'Made for Wales' qualification resources. Go back to the Resource Filter site to find relevant resources.",
              )}
              <br />
              <br />
              <a
                href={"https://filter.resources.wjec.co.uk/" + locale}
                className="text-danger"
              >
                {_("Resource filter")}
              </a>
            </div>
          ) : (
            <Carousel
              interval={null}
              controls={true}
              wrap={false}
              className="edit-resource-carousel"
            >
              <Carousel.Item>
                <h3>{_("Duplicate")}</h3>
                <p>
                  {_(
                    "Choose from an array of different resources from our resources site.",
                  )}
                </p>
                <img
                  src={ResourcesArray}
                  alt={_("Resources array")}
                  className="mx-auto d-block"
                />
              </Carousel.Item>

              <Carousel.Item>
                <h3>{_("Customise")}</h3>
                <p>
                  {_(
                    "Customise your chosen resource to fit the needs of your classroom.",
                  )}
                </p>
                <img
                  src={CustomiseResource}
                  alt={_("Customise resource")}
                  className="mx-auto d-block"
                />
              </Carousel.Item>

              <Carousel.Item>
                <h3>{_("Share")}</h3>
                <p>{_("Share your customised resource with your class.")}</p>
                <img
                  src={ShareCustomisedResource}
                  alt={_("Share customised resource")}
                  className="mx-auto d-block"
                />
              </Carousel.Item>
            </Carousel>
          )}

          <div className="d-flex justify-content-between">
            <Form className="m-2">
              <Form.Check
                inline
                checked={selectedLocale === "en"}
                onChange={() => {
                  setSelectedLocale("en");
                }}
                label={"English"}
                type={"radio"}
              />
              <Form.Check
                inline
                checked={selectedLocale === "cy"}
                onChange={() => {
                  setSelectedLocale("cy");
                }}
                label={"Cymraeg"}
                type={"radio"}
                data-testid={CY_CHECK_TEST_ID}
              />
            </Form>
            <Button
              className={`m-1 btn btn-sm px-3 ${
                isLoading ? "btn-outline-editing" : "btn-editing"
              }`}
              disabled={
                isLoading || !resource.Object.qualification?.includes("mfw")
              }
              onClick={() => {
                setIsLoading(true);
                getUserResource();
              }}
            >
              {isLoading ? <BLSpinner /> : _("Begin")}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default EditResourceModal;
